/* eslint-disable import/no-unresolved */
import { useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
// @ts-ignore
import { Box, Message } from '@zolteam/zol-admin-ui';
// Assets
import Adult from '../../../assets/icons/flightBrief/adult.svg?react';
import Airplane from '../../../assets/icons/flightBrief/airplane.svg?react';
import Animal from '../../../assets/icons/flightBrief/animal.svg?react';
import Catering from '../../../assets/icons/flightBrief/catering.svg?react';
import Child from '../../../assets/icons/flightBrief/child.svg?react';
import Hash from '../../../assets/icons/flightBrief/hash.svg?react';
import Headset from '../../../assets/icons/flightBrief/headset.svg?react';
import Infant from '../../../assets/icons/flightBrief/infant.svg?react';
import Landing from '../../../assets/icons/flightBrief/landing.svg?react';
import Location from '../../../assets/icons/flightBrief/location.svg?react';
import Phone from '../../../assets/icons/flightBrief/phone.svg?react';
import Registration from '../../../assets/icons/flightBrief/registration.svg?react';
import Seat from '../../../assets/icons/flightBrief/seat.svg?react';
import Star from '../../../assets/icons/flightBrief/star.svg?react';
import Takeoff from '../../../assets/icons/flightBrief/takeoff.svg?react';
import Transfer from '../../../assets/icons/flightBrief/transfer.svg?react';
import User from '../../../assets/icons/flightBrief/user.svg?react';
import Clock from '../../../assets/icons/clock.svg?react';
import logoHorizontalBlack from '../../../assets/logo-horizontal-black.png';
// Domain
import {
  capitalizedValue,
  formatBookingDateLong,
  formatPdfGeneratedAt,
  getAirportCode,
  getPassengersTotalCount,
  groupPassengersByCategory,
  hasPassengersByCategory,
  joinPassengerNames,
} from '../../../domain/flightBrief/flightBriefUseCases';
import { formatPersonFullName } from '../../../domain/users/userUseCases';
// Services
import { useGetFlightBrief, useGetSharedFlightBrief } from '../../../services/flightBriefs/flightBriefsQueries';
import { getErrorMessage } from '../../../utils/errors/displayError';
// Components
import Divider from '../../atoms/flightBriefPdf/Divider/Divider';
import PassengerLine from '../../atoms/flightBriefPdf/PassengerLine/PassengerLine';
import StatItem from '../../atoms/flightBriefPdf/StatItem/StatItem';
import BriefContact from '../../molecules/flightBriefPdf/BriefContact/BriefContact';
import BriefFooter from '../../molecules/flightBriefPdf/BriefFooter/BriefFooter';
import BriefHeader from '../../molecules/flightBriefPdf/BriefHeader/BriefHeader';
import BriefSection from '../../molecules/flightBriefPdf/BriefSection/BriefSection';
import BriefSubHeader from '../../molecules/flightBriefPdf/BriefSubHeader/BriefSubHeader';
import FlightBriefTripLine from '../../molecules/flightBriefPdf/FlightBriefTripLine/FlightBriefTripLine';
import HandlingColumn from '../../molecules/flightBriefPdf/HandlingColumn/HandlingColumn';
import StatRow from '../../molecules/flightBriefPdf/StatRow/StatRow';
import styles from './FlightBriefPDF.module.css';

/**
 * Flight brief PDF template rebuilt from the Figma design, composed from the
 * reusable building blocks in `./components`. Data is fetched here (the
 * boundary) and passed down to presentational pieces.
 * @param {object} props
 * @param {string} props.uuid - The uuid of the flight brief.
 * @param {string} props.locale - The locale of the flight brief.
 * @param {React.RefObject<HTMLDivElement>} props.targetRef - Ref of the PDF target element.
 * @returns {JSX.Element}
 */
function FlightBriefPDF({ uuid, locale, targetRef }) {
  const { t } = useTranslation(undefined, { lng: locale });

  const { data: flightBrief, isError, error } = useGetSharedFlightBrief(uuid);
  const { data: adminFlightBrief } = useGetFlightBrief(uuid);

  const passengersByCategory = useMemo(
    () => groupPassengersByCategory(adminFlightBrief?.passengersList),
    [adminFlightBrief?.passengersList],
  );

  if (isError) {
    return (
      <div className={styles.body}>
        <Box direction="row" justify="center">
          <div className="margin-m">
            <Message>{getErrorMessage(error.message)}</Message>
          </div>
        </Box>
      </div>
    );
  }

  const notAvailable = t('sharedFlightBrief.pdf.notAvailable');
  const hasPassengers = hasPassengersByCategory(passengersByCategory);
  const departureCode = getAirportCode(flightBrief?.departure?.airport) || notAvailable;
  const arrivalCode = getAirportCode(flightBrief?.arrival?.airport) || notAvailable;
  const dutyOfficer = adminFlightBrief?.dutyOfficer;

  /**
   * Renders one airport handling column for a given trip side.
   * @param {'departure' | 'arrival'} side - Which terminal to display.
   * @param {string} code - The airport code shown in the handling title.
   * @returns {JSX.Element}
   */
  const renderHandlingColumn = (side, code) => {
    const terminal = flightBrief?.[`${side}Terminal`];
    return (
      <HandlingColumn
        titleIcon={side === 'departure' ? <Takeoff /> : <Landing />}
        title={t(`sharedFlightBrief.pdf.handling.${side}`, { code })}
        terminalName={capitalizedValue(terminal?.name) || capitalizedValue(terminal?.identityCode) || notAvailable}
        terminal={terminal.identityCode ? terminal.identityCode : t('sharedFlightBrief.pdf.handling.mainTerminal')}
        addressIcon={<Location />}
        address={terminal?.address}
        phoneIcon={<Phone />}
        phone={terminal?.phone}
        scheduleIcon={<Clock />}
        openingTime={terminal?.openingTime}
        closingTime={terminal?.closingTime}
        meetingLabel={t('sharedFlightBrief.pdf.handling.meetingInfo')}
        meeting={terminal?.meetingInfo}
      />
    );
  };

  return createPortal(
    <div className={styles.body} ref={targetRef}>
      <div className={styles.page}>
        <BriefHeader>
          <img src={logoHorizontalBlack} alt={t('sharedFlightBrief.pdf.logoAlt')} />
        </BriefHeader>

        <div className={styles.content}>
          <BriefSubHeader
            title={t('sharedFlightBrief.pdf.briefTitle')}
            date={formatBookingDateLong(flightBrief?.departure?.date, 'eeee dd MMMM yyyy', locale)}
          />

          <FlightBriefTripLine flightBrief={flightBrief} locale={locale} />

          <StatRow footer={adminFlightBrief?.information || null}>
            <StatItem icon={<Airplane />} label={t('sharedFlightBrief.pdf.stats.aircraft')}>
              {flightBrief?.aircraft?.type || notAvailable}
            </StatItem>
            <Divider />
            <StatItem icon={<Registration />} label={t('sharedFlightBrief.pdf.stats.registration')}>
              {flightBrief?.aircraft?.registration || notAvailable}
            </StatItem>
            <Divider />
            <StatItem icon={<Hash />} label={t('sharedFlightBrief.pdf.stats.flightNumber')}>
              {flightBrief?.flightNumber || notAvailable}
            </StatItem>
            <Divider />
            <StatItem icon={<Seat />} label={t('sharedFlightBrief.pdf.stats.seats')}>
              {flightBrief?.aircraft?.seatNumber || notAvailable}
            </StatItem>
            <Divider />
            <StatItem icon={<User />} label={t('sharedFlightBrief.pdf.stats.passengers')}>
              {getPassengersTotalCount(flightBrief?.passengers, ['animals']) || notAvailable}
            </StatItem>
          </StatRow>

          <BriefContact
            operationsIcon={<Headset />}
            operations={t('sharedFlightBrief.pdf.contact.operations')}
            hotlineLabel={t('sharedFlightBrief.pdf.contact.hotline')}
            hotline={t('sharedFlightBrief.pdf.contact.hotlineNumber')}
            dutyOfficerLabel={t('sharedFlightBrief.pdf.contact.dutyOfficer')}
            dutyOfficerName={formatPersonFullName(dutyOfficer) || notAvailable}
            dutyOfficerPhone={dutyOfficer?.phone}
          />

          <BriefSection
            title={t('sharedFlightBrief.pdf.handling.title')}
            titleAlign="center"
          >
            <div className={styles.handling}>
              {renderHandlingColumn('departure', departureCode)}
              <Divider />
              {renderHandlingColumn('arrival', arrivalCode)}
            </div>
          </BriefSection>

          <StatRow label={t('sharedFlightBrief.pdf.onboard.title')} variant="onboard">
            <StatItem align="start" multiline icon={<Catering />} label={t('sharedFlightBrief.pdf.onboard.catering')}>
              {flightBrief?.extra?.catering || notAvailable}
            </StatItem>
            <Divider />
            <StatItem align="start" multiline icon={<Transfer />} label={t('sharedFlightBrief.pdf.onboard.transfer')}>
              {flightBrief?.extra?.transport || notAvailable}
            </StatItem>
            <Divider />
            <StatItem align="start" multiline icon={<Star />} label={t('sharedFlightBrief.pdf.onboard.other')}>
              {flightBrief?.extra?.other || notAvailable}
            </StatItem>
          </StatRow>

          <BriefSection title={t('sharedFlightBrief.pdf.passengers.title')} titleSize="sm">
            {hasPassengers ? (
              <>
                {passengersByCategory?.adult?.length ? (
                  <PassengerLine icon={<Adult />}>{joinPassengerNames(passengersByCategory.adult)}</PassengerLine>
                ) : null}
                <div className={styles['passengers-row']}>
                  {passengersByCategory?.child?.length ? (
                    <PassengerLine icon={<Child />}>{joinPassengerNames(passengersByCategory.child)}</PassengerLine>
                  ) : null}
                  {passengersByCategory?.infant?.length ? (
                    <PassengerLine icon={<Infant />}>{joinPassengerNames(passengersByCategory.infant)}</PassengerLine>
                  ) : null}
                  {passengersByCategory?.animal?.length ? (
                    <PassengerLine icon={<Animal />}>{joinPassengerNames(passengersByCategory.animal)}</PassengerLine>
                  ) : null}
                </div>
              </>
            ) : (
              <PassengerLine>{notAvailable}</PassengerLine>
            )}
          </BriefSection>
        </div>

        <BriefFooter
          className={styles['page-footer']}
          legal={t('sharedFlightBrief.pdf.footer.legal')}
          generatedLabel={t('sharedFlightBrief.pdf.footer.generated')}
          generatedAt={formatPdfGeneratedAt(locale)}
        />
      </div>
    </div>,
    document.body,
  );
}

export default FlightBriefPDF;
